import { useEffect, useRef } from 'react';
import connectionLostIllustration from '@/assets/suifrens/connection-lost.svg';
import { dismissNotification, showCustomNotification, showSuccessNotification } from '@/lib/notifications/notification-center';

const CONNECTION_TOAST_ID = 'connection-lost';

export const useConnectionMonitor = () => {
	const hasShownOfflineToast = useRef(false);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}
		const preloader = new Image();
		preloader.src = connectionLostIllustration;
		return () => {
			preloader.src = '';
		};
	}, []);

	useEffect(() => {
		const showOfflineToast = () => {
			if (hasShownOfflineToast.current) {
				return;
			}

			hasShownOfflineToast.current = true;

			showCustomNotification(
				(id) => (
					<div className="flex items-center gap-4 rounded-xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur">
						<img src={connectionLostIllustration} alt="Connection lost" className="size-20 animate-bounce" />
						<div className="flex flex-col">
							<p className="text-base font-semibold text-foreground">Connection lost</p>
							<p className="text-sm text-muted-foreground">We’ll keep trying to reconnect in the background.</p>
						</div>
					</div>
				),
				{
					id: CONNECTION_TOAST_ID,
					duration: Number.POSITIVE_INFINITY,
					position: 'top-center',
					onClose: () => {
						hasShownOfflineToast.current = false;
					},
				},
			);
		};

		const handleOffline = () => {
			showOfflineToast();
		};

		const handleOnline = () => {
			dismissNotification(CONNECTION_TOAST_ID);
			hasShownOfflineToast.current = false;
			showSuccessNotification({
				title: 'Back online',
				description: 'Connection restored successfully.',
				dedupeKey: 'connection-restored',
				duration: 4_000,
			});
		};

		if (typeof window !== 'undefined') {
			window.addEventListener('offline', handleOffline);
			window.addEventListener('online', handleOnline);

			if (navigator.onLine === false) {
				showOfflineToast();
			}
		}

		return () => {
			if (typeof window !== 'undefined') {
				window.removeEventListener('offline', handleOffline);
				window.removeEventListener('online', handleOnline);
			}
		};
	}, []);
};
